import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { FinancialService } from '../financial.service';
import { DivisionTurnoverTarget } from '../../models/Financial/DivisionTurnoverOverview';

interface TargetRow {
    month: number;
    label: string;
    divisions: { [division: string]: number };
}

@Component({
    selector: 'app-division-target-modal',
    templateUrl: './division-target-modal.component.html',
    styleUrls: ['./division-target-modal.component.scss'],
})
export class DivisionTargetModalComponent implements OnInit {
    @Input() year: number;
    @Input() divisions: string[] = [];
    @Input() divisionLabels: { [key: string]: string } = {};
    @Input() existingTargets: DivisionTurnoverTarget[] = [];

    saving = false;
    rows: TargetRow[] = [];

    readonly months = [
        { value: 1, label: 'Jan' },
        { value: 2, label: 'Feb' },
        { value: 3, label: 'Mar' },
        { value: 4, label: 'Apr' },
        { value: 5, label: 'May' },
        { value: 6, label: 'Jun' },
        { value: 7, label: 'Jul' },
        { value: 8, label: 'Aug' },
        { value: 9, label: 'Sep' },
        { value: 10, label: 'Oct' },
        { value: 11, label: 'Nov' },
        { value: 12, label: 'Dec' },
    ];

    constructor(
        public activeModal: NgbActiveModal,
        private financialService: FinancialService,
        private toastr: ToastrService,
    ) {}

    ngOnInit(): void {
        this.rows = this.months.map(m => {
            const divValues: { [division: string]: number } = {};
            this.divisions.forEach(div => {
                const existing = this.existingTargets.find(t => t.division === div && t.month === m.value);
                divValues[div] = existing ? existing.targetAmount : 0;
            });
            return { month: m.value, label: m.label, divisions: divValues };
        });
    }

    onValueChanged(row: TargetRow, division: string, value: string | number): void {
        const parsed = typeof value === 'string' ? parseFloat(value) : value;
        row.divisions[division] = isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
    }

    getDivisionTotal(division: string): number {
        return this.rows.reduce((sum, row) => sum + (row.divisions[division] || 0), 0);
    }

    getMonthTotal(row: TargetRow): number {
        return this.divisions.reduce((sum, div) => sum + (row.divisions[div] || 0), 0);
    }

    getGrandTotal(): number {
        return this.divisions.reduce((sum, div) => sum + this.getDivisionTotal(div), 0);
    }

    copyToAllMonths(division: string, sourceRow: TargetRow): void {
        const value = sourceRow.divisions[division] || 0;
        this.rows.forEach(row => {
            row.divisions[division] = value;
        });
    }

    save(): void {
        const targets: DivisionTurnoverTarget[] = [];
        this.rows.forEach(row => {
            this.divisions.forEach(div => {
                targets.push({
                    division: div,
                    year: this.year,
                    month: row.month,
                    targetAmount: row.divisions[div] || 0,
                });
            });
        });

        this.saving = true;
        this.financialService.upsertDivisionTurnoverTargets(targets).subscribe({
            next: () => {
                this.toastr.success('Targets saved successfully.');
                this.activeModal.close(true);
            },
            error: () => {
                this.toastr.error('Failed to save targets.');
                this.saving = false;
            },
        });
    }

    clearAll(): void {
        this.rows.forEach(row => {
            this.divisions.forEach(div => {
                row.divisions[div] = 0;
            });
        });
    }
}
